import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// Enables dayjs.utc(...) so formatDateUtc can render BE timestamps that pack
// wall-clock-as-UTC (e.g. sales_invoice_datetime stores POS wall-clock as a
// UTC instant) without the browser timezone re-shifting them. Side-effect
// extend is intentional and idempotent — same pattern as registering a global
// dayjs plugin at module init.
dayjs.extend(utc);

export const DateFormat = {
	// Indonesian standard: numeric day/month/year (e.g. 27/05/2026). List/table
	// cells across the app rely on this default — using `MMM` surfaced English
	// month abbreviations like "27/May/2026" (ClickUp 86exr73rc).
	General: "DD/MM/YYYY",
	DateWithTime: "DD/MM/YYYY HH:mm",
	// `Full*` formats keep the spelled-out month for verbose display contexts.
	FullDate: "DD/MMMM/YYYY",
	FullDateTime: "DD/MMMM/YYYY HH:mm",
	TimeOnly: "HH:mm",
} as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const JUST_NOW_LABEL = "Baru saja";

// Portal style: "10 menit lalu".
export function formatRelativeTime(date: dayjs.ConfigType): string {
	const elapsedMs = Date.now() - dayjs(date).valueOf();
	if (elapsedMs < MINUTE_MS) return JUST_NOW_LABEL;
	if (elapsedMs < HOUR_MS)
		return `${Math.floor(elapsedMs / MINUTE_MS)} menit lalu`;
	if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)} jam lalu`;
	return `${Math.floor(elapsedMs / DAY_MS)} hari lalu`;
}

export function formatDate(date: dayjs.ConfigType, format?: string): string {
	return dayjs(date).format(format ?? DateFormat.General);
}

/**
 * Use for BE timestamps where the wall-clock is already packed as a UTC instant
 * (project convention: `*_datetime` fields like sales_invoice_datetime,
 * recon_datetime — distinct from the `*_datetime_utc` siblings which are real
 * UTC). Plain formatDate would re-shift these by the browser TZ and produce a
 * different value on every machine.
 */
export function formatDateUtc(date: dayjs.ConfigType, format?: string): string {
	return dayjs.utc(date).format(format ?? DateFormat.General);
}

/**
 * Use for BE timestamps that already carry their real timezone offset (e.g.
 * sales_invoice_datetime serialized as `2026-06-19T23:49:00+07:00`). Renders
 * the wall-clock of that embedded offset as-is, without re-shifting to UTC
 * (formatDateUtc) or to the browser TZ (formatDate).
 */
export function formatDatePreserveZone(
	date: dayjs.ConfigType,
	format?: string,
): string {
	const fmt = format ?? DateFormat.General;

	if (typeof date === "string") {
		const match = date.match(/(Z)$|([+-])(\d{2}):?(\d{2})$/);
		if (match) {
			const offsetMinutes = match[1]
				? 0
				: (match[2] === "-" ? -1 : 1) *
					(Number(match[3]) * 60 + Number(match[4]));
			return dayjs(date).utcOffset(offsetMinutes).format(fmt);
		}
	}

	return dayjs(date).format(fmt);
}

/**
 * Preserve the timezone when converting to ISO string
 */
export function toISOStringPreserve(date: Date): string {
	const localDate = new Date(
		date.getTime() - date.getTimezoneOffset() * 60_000,
	);
	return localDate.toISOString();
}

/**
 * Preserve the timezone when converting to Date
 */
export function fromISOStringPreserve(isoString: string): Date {
	const utcDate = new Date(isoString);
	return new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60_000);
}

export const INDONESIAN_SHORT_MONTH_NAMES = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"Mei",
	"Jun",
	"Jul",
	"Agu",
	"Sep",
	"Okt",
	"Nov",
	"Des",
] as const;

export const INDONESIAN_MONTH_NAMES = [
	"Januari",
	"Februari",
	"Maret",
	"April",
	"Mei",
	"Juni",
	"Juli",
	"Agustus",
	"September",
	"Oktober",
	"November",
	"Desember",
] as const;

export const INDONESIAN_SHORT_DAY_NAMES = [
	"Min",
	"Sen",
	"Sel",
	"Rab",
	"Kam",
	"Jum",
	"Sab",
] as const;

// "4 Oktober 2026".
export function formatIndonesianLongDate(date: dayjs.ConfigType): string {
	const value = dayjs(date);
	return `${value.date()} ${INDONESIAN_MONTH_NAMES[value.month()]} ${value.year()}`;
}

// "4 Okt 2026".
export function formatIndonesianShortDate(date: dayjs.ConfigType): string {
	const value = dayjs(date);
	return `${value.date()} ${INDONESIAN_SHORT_MONTH_NAMES[value.month()]} ${value.year()}`;
}

// "07/10" from an ISO date.
export const formatDayMonth = (isoDate: string) =>
	isoDate && dayjs(isoDate).isValid() ? dayjs(isoDate).format("DD/MM") : "";
